import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useFormDisable } from '../../../../../../shared/form-new/form'
import { FormNewLabel } from '../../../../../../shared/form-new/form-label'
import { StaffTag } from '../../../../../../shared/sso-workspace/workspace-shell'
import { OrgPicker } from '../../../../../../shared/org-search/org-picker'

// Trimmed, lower-cased email, or null -- the test allowlist's own normaliser
// (spec 4.x): looser than normalizeDomain since an allowlist entry is a full
// address, not a bare domain, so it has no "must contain a dot after an @"
// shape check beyond what the operator typed.
export const normalizeEmail = raw => {
  const v = String(raw || '').trim().toLowerCase()
  return v || null
}

export const DRIVER_OPTIONS = [
  { value: 'oidc', label: 'OpenID Connect' },
  { value: 'apple', label: 'Apple' },
  { value: 'facebook', label: 'Facebook' }
]

export const CONNECTION_OPTIONS = [
  { value: 'own_registration', label: 'Customer\'s own app registration' },
  { value: 'shared_app', label: 'EducationXR shared app' }
]

// The workspace header's subtitle: protocol · app registration · identifier,
// all from the row. The registration applies to OpenID Connect only (Apple
// and Facebook have none). No provider "kind" (e.g. the identity provider's
// product name): nothing on the row says it without guessing from names.
const REGISTRATION_SUBTITLE = { own_registration: 'own app registration', shared_app: 'EducationXR shared app' }
export const connectionSubtitle = provider => {
  if (!provider) return null
  const protocol = (DRIVER_OPTIONS.find(o => o.value === provider.driver) || {}).label || provider.driver
  const registration = provider.driver === 'oidc' ? REGISTRATION_SUBTITLE[provider.connection] : null
  const text = [protocol, registration].filter(Boolean).join(' · ')
  if (!text && !provider.identifier) return null
  return (
    <React.Fragment>
      {text}
      {text && provider.identifier && ' · '}
      {provider.identifier && <code>{provider.identifier}</code>}
    </React.Fragment>
  )
}

// Status is the row's `active` boolean, shown as the mockup's select. The
// select's values are strings, so this maps them both ways and the form (and
// the PUT) keeps a real boolean.
export const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Active: people can sign in with it' },
  { value: 'false', label: 'Inactive: nobody can sign in with it' }
]

// noRoute: why an ACTIVE connection still reaches nobody (audit B3: a
// connection with no bound domain that isn't marked public is offered to no
// one), shown under the select while it says Active, with a way to the
// place that fixes it (onFix).
export const FormNewActiveSelect = ({ label, noRoute, fixText, onFix }) => {
  const { control } = useFormContext()
  const { field } = useController({ name: 'active', control })
  const formDisabled = useFormDisable()
  return (
    <>
      <FormNewLabel name="active" label={label}>
        <select className="form-select" disabled={formDisabled} value={field.value ? 'true' : 'false'} onChange={e => field.onChange(e.target.value === 'true')} onBlur={field.onBlur}
          aria-describedby={field.value && noRoute ? 'active-no-route' : undefined}>
          {ACTIVE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </FormNewLabel>
      {field.value && noRoute &&
        <div id="active-no-route" className="sw-hint sw-warn mt-1" role="note">
          {noRoute}
          {onFix && <> <button type="button" className="btn btn-link p-0 align-baseline" onClick={onFix}>{fixText || 'Bind a domain'}</button></>}
        </div>}
    </>
  )
}

// A write-only credential: a client secret or a private key. The server
// never returns the stored value (agg.go ClientSecret/PrivateKeyPEM never
// reach JSON), so this always renders blank -- `hasValue` (hasClientSecret /
// hasPrivateKey) is the only signal that something is already stored, and a
// blank submit means "leave it alone" (spec 5.1/5.2).
export const FormNewSecret = ({ name, label, hasValue, textarea, subtext }) => {
  const { register } = useFormContext()
  const formDisabled = useFormDisable()

  const placeholder = hasValue ? `leave blank to keep the stored ${textarea ? 'key' : 'secret'}` : (label || name)

  return (
    <FormNewLabel name={name} label={label} subtext={subtext} secret>
      {textarea
        ? <textarea className="form-control" placeholder={placeholder} {...register(name)} disabled={formDisabled} />
        : <input className="form-control" type="password" placeholder={placeholder} {...register(name)} disabled={formDisabled} />}
    </FormNewLabel>
  )
}

// certificatePEM is the one credential-adjacent field that is NOT write-only
// -- it round-trips through JSON (agg.go), so unlike FormNewSecret it just
// shows whatever is stored.
// secret: never shown in the unsaved-changes list (the certificate).
export const FormNewTextArea = ({ name, label, subtext, placeholder, secret }) => {
  const { register } = useFormContext()
  const formDisabled = useFormDisable()
  return (
    <FormNewLabel name={name} label={label} subtext={subtext} secret={secret}>
      <textarea className="form-control" placeholder={placeholder || label || name} {...register(name)} disabled={formDisabled} />
    </FormNewLabel>
  )
}

// organizationID is a *uint on the backend (agg.go's UpdateModelFromAgg
// copies it as a plain "what the admin picked" value, not a "blank means
// leave alone" field) -- an empty string would fail to decode, so this
// stores a real number or null via useController rather than the plain
// register() a <select> normally gets, which would only ever hand back a
// string. The owner is picked from content's organization search
// (OrgPicker; owner 2026-10-13: never a list of every organization).
const PLATFORM_HELD = { value: '', label: 'Platform-held' }

export const FormNewOrganizationSelect = () => {
  const { control } = useFormContext()
  const { field } = useController({ name: 'organizationID', control })
  const formDisabled = useFormDisable()

  const handleChange = value => field.onChange(value ? Number(value) : null)

  return (
    <FormNewLabel name="organizationID" label={<React.Fragment>Owner<StaffTag /></React.Fragment>} subtext="Platform-held when no organization owns this connection.">
      <OrgPicker id="organizationID" label="Owner" disabled={formDisabled} value={field.value ? String(field.value) : ''} allOption={PLATFORM_HELD} onChange={handleChange} />
    </FormNewLabel>
  )
}

// The labels the sections show, for naming fields a blocked save needs.
// clientID reads differently per driver (section-identity.js).
const FIELD_LABELS = {
  providerName: 'name', displayName: 'sign-in button label', driver: 'protocol', connection: 'app registration',
  issure: 'issuer', clientID: 'client id', clientAuthMethod: 'client auth method', expiresAt: 'expires',
  contactName: 'contact name', contactEmail: 'contact email', helpUrl: 'access-request link', helpText: 'help text',
  scopesExtra: 'additional scopes', iconSVG: 'icon svg', signoutURL: 'sign-out endpoint', testAllowlist: 'test allowlist'
}
const DRIVER_FIELD_LABELS = { apple: { clientID: 'service id' }, facebook: { clientID: 'app id' } }

export const fieldLabel = (key, driver) => ((DRIVER_FIELD_LABELS[driver] || {})[key]) || FIELD_LABELS[key] || key

// What the save bar says when validation blocks a save: missing required
// fields first, then any field that failed another rule.
export const invalidFieldsMessage = (errors, driver) => {
  const keys = Object.keys(errors || {})
  const required = keys.filter(k => errors[k] && errors[k].type === 'required').map(k => fieldLabel(k, driver))
  const other = keys.filter(k => !(errors[k] && errors[k].type === 'required')).map(k => fieldLabel(k, driver))
  return [
    required.length ? `Fill in the required fields: ${required.join(', ')}` : null,
    other.length ? `Check these fields: ${other.join(', ')}` : null
  ].filter(Boolean).join('. ')
}

// Provider fields the workspace edits through plain text inputs, selects
// and textareas. The API sends the nullable ones (*string in agg.go) as
// null, and write-only ones may be missing, but an input can only hold ''.
// react-hook-form compares against the loaded value, so a null field an
// admin types into and clears again would count as an unsaved change. The
// workspace hands FormNew '' for these, and puts back the loaded null (or
// absence) for any left blank, so the PUT body is what the old form sent.
export const PROVIDER_TEXT_FIELDS = [
  'identifier', 'providerName', 'displayName', 'driver', 'connection', 'issure', 'clientID', 'clientAuthMethod',
  'clientSecretNew', 'privateKeyPEMNew', 'certificatePEM', 'signoutURL', 'scopesExtra', 'tenantClaim', 'tenantValue',
  'contactName', 'contactEmail', 'helpUrl', 'helpText', 'iconSVG', 'appleTeamID', 'appleKeyID'
]

export const toProviderFormState = provider => {
  const state = { ...provider }
  PROVIDER_TEXT_FIELDS.forEach(key => { if (state[key] === null || state[key] === undefined) state[key] = '' })
  return state
}

export const fromProviderFormState = (values, loaded = {}) => {
  const body = { ...values }
  PROVIDER_TEXT_FIELDS.forEach(key => {
    if (body[key] !== '') return
    if (loaded[key] === null) body[key] = null
    else if (loaded[key] === undefined) delete body[key]
  })
  // A present `domains` list replaces the provider's domain bindings on the
  // server (provider/service.go); a missing key leaves them alone. The
  // workspace doesn't edit domains (the Domains page does), so sending the
  // loaded list back would undo any domain change made since it loaded.
  delete body.domains
  delete body.pendingDomains
  // Public is set only on the Sign-in methods page (PUT /provider/:id/public);
  // the generic save can't change it, so it isn't sent.
  delete body.public
  return body
}
